import { Dispatch, SetStateAction, useCallback, useMemo } from 'react'

import { useFacilityCSVProcessor } from '../hooks/useFacilityCSVProcessor'
import { getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { UseFormMethods } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { HiOutlineDownload } from 'react-icons/hi'

import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'

import { Button as DarkButton, DownloadIcon } from '@hivewatch/dark'

import {
  Button,
  FormFileUploadControl,
  StyledFormInputControl,
  Table,
  TableBody,
  TableHeader,
} from '@/components/ui'
import { isValidFileType } from '@/features/device-crud'
import { tVar } from '@/utils/i18n'

import { FormInputsIProps } from '../types/types'
import { CSVParseResultI, downloadCSVTemplate } from '../utils/csvParser'
import { getTimezoneFromCoordinates } from '../utils/geocoding'
import { useFacilityCrudColumns } from '../utils/tableColumns'

interface BulkUploadFacilitiesFormIProps {
  form: UseFormMethods<FormInputsIProps>
  parseResult: CSVParseResultI | null
  onParseResultChange: Dispatch<SetStateAction<CSVParseResultI | null>>
  /**
   * Rebrand: keys the hint/progress copy and the file field to the theme. Omit
   * to keep the legacy appearance untouched.
   */
  colorScheme?: 'dark' | 'light'
}

export const BulkUploadFacilitiesForm = ({
  form,
  parseResult,
  onParseResultChange,
  colorScheme,
}: BulkUploadFacilitiesFormIProps) => {
  const { t } = useTranslation()
  const { register, watch, trigger } = form
  const fileUpload = watch('file')

  // Use custom hook to handle CSV processing, geocoding, and timezone lookup
  const { isLoading, isGeocoding, geocodingProgress } = useFacilityCSVProcessor(
    fileUpload,
    onParseResultChange
  )

  // Handle address updates from AddressSearchInput
  const handleUpdateAddress = useCallback(
    (
      rowIndex: number,
      address: { value: string; latitude?: number; longitude?: number }
    ) => {
      onParseResultChange((prev) => {
        if (!prev) return prev

        const updatedData = [...prev.data]
        const facility = { ...updatedData[rowIndex] }

        // Update address and coordinates
        facility.address = address.value
        facility.lat = address.latitude?.toString() || ''
        facility.lon = address.longitude?.toString() || ''

        // Update timezone based on coordinates
        if (address.latitude && address.longitude) {
          const timezone = getTimezoneFromCoordinates(
            address.latitude.toString(),
            address.longitude.toString()
          )
          if (timezone) {
            facility.timezone = timezone
          }
        }

        // Clear any geocoding errors since we have new valid data
        facility.geocodingError = undefined
        updatedData[rowIndex] = facility

        return { ...prev, data: updatedData }
      })
    },
    [onParseResultChange]
  )

  const columns = useFacilityCrudColumns({
    onUpdateAddress: handleUpdateAddress,
  })

  const tableData = useMemo(() => {
    return parseResult?.data || []
  }, [parseResult])

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  const mutedColor = colorScheme ? `text.secondary.${colorScheme}` : 'gray.600'
  const subtleColor = colorScheme ? `text.tertiary.${colorScheme}` : 'gray.500'

  return (
    <Box px={colorScheme ? '0' : '6'}>
      <Box mb={3}>
        {colorScheme ? (
          <DarkButton
            colorScheme={colorScheme}
            leftIcon={<DownloadIcon colorScheme={colorScheme} size={16} />}
            onClick={downloadCSVTemplate}
            size='sm'
            variant='secondary'
          >
            {t('facilities:crud_download_csv_template')}
          </DarkButton>
        ) : (
          <Button
            leftIcon={<HiOutlineDownload size='20px' />}
            name='asd'
            onClick={downloadCSVTemplate}
            size='sm'
            variant='secondary'
          >
            {t('facilities:crud_download_csv_template')}
          </Button>
        )}
        <Text color={mutedColor} fontSize='12px' mt={2}>
          {t('facilities:crud_csv_template_hint')}
        </Text>
      </Box>

      <StyledFormInputControl mb={3}>
        <Tooltip
          isDisabled={isValidFileType(fileUpload)}
          label={t('facilities:crud_csv_file_type_tooltip')}
          shouldWrapChildren
        >
          <FormFileUploadControl
            accept='.csv'
            colorScheme={colorScheme}
            dataTestId='createFacility_modal_fileUpload'
            errorMessage={
              fileUpload &&
              !isValidFileType(fileUpload) &&
              t('facilities:crud_csv_file_invalid_format')
            }
            id='file'
            inputRef={register({ required: true })}
            isInvalid={!isValidFileType(fileUpload)}
            label={t('facilities:crud_csv_label')}
            onFileRemoved={() => {
              trigger(['file'])
              onParseResultChange(null)
            }}
            placeholder={t('facilities:crud_csv_attach_placeholder')}
          />
        </Tooltip>
      </StyledFormInputControl>

      {/* Loading indicator */}
      {isLoading && (
        <Box mb={3}>
          <Text color={mutedColor} fontSize='sm'>
            {t('common:parsing_csv')}
          </Text>
        </Box>
      )}

      {/* Geocoding progress indicator */}
      {isGeocoding && (
        <Box mb={3}>
          <Text color={mutedColor} fontSize='sm' fontWeight='medium'>
            {tVar(t, 'facilities:crud_geocoding_addresses', {
              current: geocodingProgress.current,
              total: geocodingProgress.total,
            })}
          </Text>
          <Text color={subtleColor} fontSize='xs' mt={1}>
            {t('facilities:crud_geocoding_converting')}
          </Text>
        </Box>
      )}

      {/* Display parsing errors */}
      {parseResult && parseResult.errors.length > 0 && (
        <Alert mb={3} status='error'>
          <AlertIcon />
          <VStack align='start' spacing={1}>
            <AlertTitle>{t('common:csv_parsing_errors')}</AlertTitle>
            <AlertDescription>
              {parseResult.errors.map((error, index) => (
                <Text fontSize='sm' key={index}>
                  • {error}
                </Text>
              ))}
            </AlertDescription>
          </VStack>
        </Alert>
      )}

      {/* Display Table with pasred facility data */}
      {parseResult && parseResult.data.length > 0 && (
        <Box mb={3}>
          <Table
            tableContainerStyles={{
              overflowY: 'auto',
            }}
          >
            <TableHeader table={table} />
            <TableBody rowModel={table.getRowModel()} />
          </Table>
        </Box>
      )}
    </Box>
  )
}
